/**
 * The assessment stream — the indexed sequence of phrases that actually count
 * toward complexity adjustment.
 *
 * This exists because the render queue and the assessment stream are NOT the
 * same sequence. `PhraseManager` renders a circular buffer that may contain
 * prefetched phrases and injected rest phrases; only ordinary played phrases
 * are assessments. PAD is counted in assessment phrases, so threading a verdict
 * to "the phrase after next" must ignore any rest phrases in between.
 *
 * Per the engineering spec: "Do not implement PAD as '+PAD positions in the
 * rendered queue.' Maintain the assessment stream as its own indexed sequence;
 * rest phrases are interleaved for playback but are not members of it."
 *
 * Indices are 1-based and contiguous. Rest phrases are recorded (so the
 * controller can recognise them on completion) but consume no index.
 */

import { Band, Verdict } from './complexityModel.types';

/** How a phrase participates in complexity adjustment. */
export enum PhraseKind {
  /** An ordinary phrase: gets an index, a verdict, and moves the band. */
  Assessment = 'assessment',
  /** An injected rest phrase: inert. No index, no verdict, no PAD slot. */
  Rest = 'rest',
}

/** A phrase registered with the stream. */
export interface StreamEntry {
  uuid: string;
  kind: PhraseKind;
  /** 1-based assessment index. `undefined` for rest phrases. */
  idx?: number;
  /** Verdict once graded. `undefined` until then, always `undefined` for rest. */
  verdict?: Verdict;
  /** Complexity error tally that produced the verdict (red 1.0 + yellow 0.5). */
  errorTotal?: number;
  /** Band this phrase was actually drawn from, recorded at plan time.
   *
   *  The trajectory can be recomputed from the verdicts at any point, but only
   *  for assessment indices — and only forwards. Consumers that need "what band
   *  was the phrase now playing drawn from?" (the chevron CAT gate and the
   *  header progress bar) must read it back per phrase, including for rest
   *  phrases, which have no index at all. Hence it is stored, not derived.
   *
   *  Carries `position` as well as the edges: the CAT gate compares against the
   *  position, which is not recoverable from `crMin` without knowing the tier's
   *  anchoring. */
  band?: Readonly<Band>;
}

/**
 * The verdict a skipped assessment index is backfilled with.
 *
 * Yellow is the only honest choice: it is the model's "band HOLDS" verdict, so
 * a phrase we never saw graded moves the band neither up nor down. Green or Red
 * would invent a performance the user never gave.
 */
const GAP_FILL_VERDICT = Verdict.Yellow;

export class AssessmentStream {
  private readonly entries: StreamEntry[] = [];
  private readonly byUuid = new Map<string, StreamEntry>();
  /** Verdicts in assessment order; `verdicts[i]` belongs to index `i + 1`. */
  private readonly verdicts: Verdict[] = [];
  private nextIdx = 1;
  private readonly log: (message: string) => void;

  /**
   * @param logger  Where anomalies go. These are all "should not happen" paths
   *                that deliberately do not throw (see {@link register}), so
   *                without a logger they are invisible.
   */
  constructor(logger?: (message: string) => void) {
    this.log = logger ?? (() => undefined);
  }

  /**
   * Register a phrase as it enters playback.
   *
   * Never throws. These calls originate inside Phaser callbacks — the phrase
   * request path and the phrase-end handler — where an exception does not just
   * lose one phrase's bookkeeping but unwinds the handler that drives playback
   * itself. A retried or duplicated `sequenceId` must cost at most its own
   * phrase, never the rest of the session. Anomalies are logged and skipped.
   *
   * @returns the assigned 1-based assessment index, or `undefined` for a rest
   *          phrase (which deliberately consumes no index). A duplicate
   *          registration returns the index the phrase already holds, so the
   *          caller sees the same answer it did the first time.
   */
  register(uuid: string, kind: PhraseKind, band?: Readonly<Band>): number | undefined {
    const existing = this.byUuid.get(uuid);
    if (existing !== undefined) {
      // Idempotent rather than additive: consuming a second index here would
      // shift every later phrase's index by one and silently mis-thread PAD for
      // the remainder of the session, which is far worse than the duplicate.
      this.log(
        `phrase ${uuid} registered twice — keeping the original ` +
          `${existing.kind === PhraseKind.Rest ? 'rest entry' : `idx=${String(existing.idx)}`}`,
      );
      return existing.idx;
    }

    const entry: StreamEntry =
      kind === PhraseKind.Assessment
        ? { uuid, kind, idx: this.nextIdx++, band }
        : { uuid, kind, band };

    this.entries.push(entry);
    this.byUuid.set(uuid, entry);
    return entry.idx;
  }

  /**
   * Record the outcome of a graded assessment phrase.
   *
   * Never throws, for the reasons in {@link register}. Unknown phrases, rest
   * phrases and repeat grades are logged and skipped.
   *
   * An out-of-order grade is the one case that is not simply skipped. A grade
   * arriving for an index PAST the next expected one means an earlier phrase
   * never got graded, and skipping would leave `verdicts` permanently short —
   * every subsequent grade would then be out-of-order too, so one dropped
   * phrase would stop the band moving for the rest of the session. The gap is
   * backfilled with {@link GAP_FILL_VERDICT} instead, keeping the stream
   * contiguous and grading alive.
   */
  grade(uuid: string, verdict: Verdict, errorTotal: number): void {
    const entry = this.byUuid.get(uuid);
    if (entry === undefined) {
      this.log(`cannot grade unregistered phrase ${uuid} — skipped`);
      return;
    }
    if (entry.kind === PhraseKind.Rest) {
      this.log(`rest phrase ${uuid} must never receive a verdict — skipped`);
      return;
    }
    if (entry.verdict !== undefined) {
      this.log(`phrase ${uuid} already graded ${entry.verdict} — skipped`);
      return;
    }

    const expected = this.verdicts.length + 1;
    if (entry.idx === undefined) {
      this.log(`assessment phrase ${uuid} has no index — skipped`);
      return;
    }
    if (entry.idx < expected) {
      // Behind the stream: its slot has already been filled, most likely by the
      // backfill below. Re-recording would corrupt the alignment between
      // `verdicts[i]` and index `i + 1`.
      this.log(
        `stale grade for ${uuid} — idx=${entry.idx} already passed (at ${expected}) — skipped`,
      );
      return;
    }
    if (entry.idx > expected) {
      const missing = entry.idx - expected;
      this.log(
        `out-of-order grade for ${uuid} — expected idx=${expected}, got ${entry.idx}; ` +
          `backfilling ${missing} ungraded ${missing === 1 ? 'index' : 'indices'} ` +
          `with ${GAP_FILL_VERDICT} (band holds) to keep the stream contiguous`,
      );
      for (let i = 0; i < missing; i += 1) this.verdicts.push(GAP_FILL_VERDICT);
    }

    entry.verdict = verdict;
    entry.errorTotal = errorTotal;
    this.verdicts.push(verdict);
  }

  /** Verdicts in assessment order — the complexity model's input. */
  getVerdicts(): readonly Verdict[] {
    return this.verdicts;
  }

  /** Highest assessment index graded so far (0 when none). */
  getGradedCount(): number {
    return this.verdicts.length;
  }

  /** Assessment indices assigned so far, graded or not. */
  getRegisteredCount(): number {
    return this.nextIdx - 1;
  }

  /**
   * The index the next assessment phrase will receive. This is the index whose
   * band the request path must resolve.
   */
  getNextIdx(): number {
    return this.nextIdx;
  }

  /** Assessment phrases played since the last rest phrase (or session start). */
  getAssessmentsSinceLastRest(): number {
    let count = 0;
    for (let i = this.entries.length - 1; i >= 0; i -= 1) {
      if (this.entries[i].kind === PhraseKind.Rest) break;
      count += 1;
    }
    return count;
  }

  isRest(uuid: string): boolean {
    return this.byUuid.get(uuid)?.kind === PhraseKind.Rest;
  }

  getEntry(uuid: string): Readonly<StreamEntry> | undefined {
    return this.byUuid.get(uuid);
  }

  /** All entries in playback order, rest phrases included. For telemetry. */
  getEntries(): readonly Readonly<StreamEntry>[] {
    return this.entries;
  }

  /** Clear all state — used on tier change, where the scene is reused. */
  reset(): void {
    this.entries.length = 0;
    this.verdicts.length = 0;
    this.byUuid.clear();
    this.nextIdx = 1;
  }
}
